/**
 * useTestMappingStudio.ts
 * Admin → Test Mapping (master-detail redesign, v1.0.34) — data hook for the
 * page: loads everything in one call, exposes the mutations (map / confirm /
 * notes / unmap) with a reload-after-write policy (the dataset is tiny —
 * ~13 tests, ~99 items), and caches the per-test suggestions from ONE
 * /suggestions round trip (the detail pane looks its test up by key).
 *
 * All API access goes through services/testMappingService.ts.
 */

import { useCallback, useEffect, useReducer, useState } from 'react';
import {
  fetchMappingStudio,
  fetchMappingSuggestions,
  createMapping,
  updateMapping,
  deleteMapping,
  type MappingStudioData,
} from '../../../services/testMappingService';
import { suggestionsReducer } from './mappingModel';

export function useTestMappingStudio() {
  const [data, setData] = useState<MappingStudioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, dispatchSuggestions] = useReducer(suggestionsReducer, []);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [suggestionsLoaded, setSuggestionsLoaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      setError(null);
      const fresh = await fetchMappingStudio();
      setData(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load test mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  /** Create-or-remap, then reload so rows/stats/chips all refresh. */
  const mapTest = useCallback(async (
    testKey: string,
    testCatalogItemKey: string,
    opts: { notes?: string | null; isConfirmed?: boolean } = {},
  ) => {
    await createMapping({ testKey, testCatalogItemKey, ...opts });
    dispatchSuggestions({ type: 'accepted', testKey });
    await reload();
  }, [reload]);

  const setConfirmed = useCallback(async (mapKey: string, isConfirmed: boolean) => {
    await updateMapping(mapKey, { isConfirmed });
    await reload();
  }, [reload]);

  const setNotes = useCallback(async (mapKey: string, notes: string | null) => {
    await updateMapping(mapKey, { notes });
    await reload();
  }, [reload]);

  const unmap = useCallback(async (mapKey: string) => {
    await deleteMapping(mapKey);
    await reload();
  }, [reload]);

  /**
   * Fetch the suggestions cache (idempotent — one round trip per page visit;
   * the detail pane calls this when an unmapped test is selected).
   */
  const loadSuggestions = useCallback(async () => {
    if (suggestionsLoaded || suggestionsLoading) return;
    setSuggestionsLoading(true);
    try {
      const rows = await fetchMappingSuggestions();
      dispatchSuggestions({ type: 'loaded', suggestions: rows });
      setSuggestionsLoaded(true);
    } catch {
      dispatchSuggestions({ type: 'clear' });
    } finally {
      setSuggestionsLoading(false);
    }
  }, [suggestionsLoaded, suggestionsLoading]);

  return {
    data,
    loading,
    error,
    reload,
    mapTest,
    setConfirmed,
    setNotes,
    unmap,
    suggestions,
    suggestionsLoading,
    suggestionsLoaded,
    loadSuggestions,
  };
}

export type TestMappingStudio = ReturnType<typeof useTestMappingStudio>;
